import { useEffect, useRef } from "react";

import {
  registerDrawSurface,
  setDrawSurfaceActive,
  unregisterDrawSurface,
} from "./drawLock";
import type { DrawSurfaceAdapter, DrawSurfaceToken } from "./types";

/**
 * Registers a canvas with the draw lock for as long as it is mounted.
 *
 * The adapter is held in a ref and invoked through stable wrappers, so a
 * surface is registered exactly once per mount no matter how often its
 * callbacks are re-created by rendering.
 */
export function useDrawSurface(
  adapter: DrawSurfaceAdapter,
  isActive: boolean,
): void {
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  const tokenRef = useRef<DrawSurfaceToken | null>(null);
  const id = adapter.id;

  useEffect(() => {
    const token = registerDrawSurface({
      id,
      canEngage: () => adapterRef.current.canEngage(),
      containsPoint: (p) => adapterRef.current.containsPoint(p),
      ownsElement: (el) => adapterRef.current.ownsElement?.(el) ?? false,
      engage: (p) => adapterRef.current.engage(p),
      release: (reason, cancel) => adapterRef.current.release(reason, cancel),
    });
    tokenRef.current = token;
    return () => {
      tokenRef.current = null;
      unregisterDrawSurface(token);
    };
  }, [id]);

  useEffect(() => {
    const token = tokenRef.current;
    if (!token) return;
    setDrawSurfaceActive(token, isActive);
  }, [isActive]);
}
